/**
 * Expertise & Authority Component
 * Implementa señales E-E-A-T (Experience, Expertise, Authoritativeness, Trustworthiness)
 * para contenido YMYL (Your Money Your Life)
 */

import React from 'react';
import { Award, BookOpen, Users, Shield, CheckCircle, Star } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface ExpertiseAuthorityProps {
  showFounderProfile?: boolean;
  showCertifications?: boolean;
  showStatistics?: boolean;
  showQualityAssurance?: boolean;
  pageType?: 'home' | 'about' | 'city' | 'therapist';
}

const ExpertiseAuthority: React.FC<ExpertiseAuthorityProps> = ({
  showFounderProfile = true,
  showCertifications = true,
  showStatistics = true,
  showQualityAssurance = true,
  pageType = 'home'
}) => {
  return (
    <div className="py-16 bg-gradient-to-br from-muted/30 to-background">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Trusted Global Mental Health Platform
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Founded by renowned experts, certified by international standards, 
              trusted by thousands worldwide
            </p>
          </div>
          
          {/* Founder Profile - Dr. José Luis Marín */}
          {showFounderProfile && (
            <Card className="mb-8 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
              <CardContent className="p-8 md:p-10">
                <div className="flex flex-col md:flex-row gap-8 items-start">
                  <div className="flex-shrink-0">
                    <div className="w-24 h-24 bg-primary/20 rounded-full flex items-center justify-center">
                      <Award className="w-12 h-12 text-primary" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-2xl md:text-3xl font-bold mb-2">
                          Dr. José Luis Marín López
                        </h3>
                        <p className="text-lg text-muted-foreground">
                          Founder & Chief Training Officer
                        </p>
                      </div>
                      <Badge variant="secondary" className="hidden md:flex">
                        40+ Years Experience
                      </Badge>
                    </div>
                    
                    <p className="text-foreground/90 leading-relaxed mb-6">
                      Renowned psychiatrist and psychotherapist with over four decades of experience 
                      in mental health. Creator of <strong>Brief Psychotherapy methodology</strong>, 
                      adopted internationally and taught to over 30,000 mental health professionals worldwide.
                    </p>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-sm">Academic Excellence</div>
                          <div className="text-sm text-muted-foreground">
                            MD from Complutense University of Madrid. 
                            Psychiatry Specialization, University of Ghent, Belgium.
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-sm">International Leadership</div>
                          <div className="text-sm text-muted-foreground">
                            Former President, Spanish Society of Psychosomatic Medicine. 
                            Founder, International Forum for Psychotherapy Training.
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-sm">Research Pioneer</div>
                          <div className="text-sm text-muted-foreground">
                            Groundbreaking research on chronic stress, developmental trauma, 
                            fibromyalgia, and psychosomatic medicine.
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-sm">Global Training Impact</div>
                          <div className="text-sm text-muted-foreground">
                            Trained 30,000+ mental health professionals across 50+ countries. 
                            Master's Degree in Brief Psychotherapy recognized worldwide.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Certifications & Standards */}
          {showCertifications && (
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card className="text-center">
                <CardContent className="pt-8 pb-6">
                  <Shield className="w-12 h-12 text-primary mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Certified Professionals</h3>
                  <p className="text-sm text-muted-foreground">
                    All therapists complete the Master's Degree in Brief Psychotherapy 
                    from formacionpsicoterapia.com
                  </p>
                </CardContent>
              </Card>
              
              <Card className="text-center">
                <CardContent className="pt-8 pb-6">
                  <BookOpen className="w-12 h-12 text-primary mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Evidence-Based Methods</h3>
                  <p className="text-sm text-muted-foreground">
                    Brief Psychotherapy combines CBT, psychodynamic, and humanistic approaches 
                    with proven effectiveness
                  </p>
                </CardContent>
              </Card>
              
              <Card className="text-center">
                <CardContent className="pt-8 pb-6">
                  <Award className="w-12 h-12 text-primary mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">International Recognition</h3>
                  <p className="text-sm text-muted-foreground">
                    Recognized by mental health organizations and professional bodies worldwide
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Statistics & Trust Signals */}
          {showStatistics && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card>
                <CardContent className="pt-6 pb-6 text-center">
                  <div className="text-3xl md:text-4xl font-bold text-primary mb-2">5,000+</div>
                  <div className="text-sm text-muted-foreground">Verified Therapists</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6 pb-6 text-center">
                  <div className="text-3xl md:text-4xl font-bold text-primary mb-2">150+</div>
                  <div className="text-sm text-muted-foreground">Countries Served</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6 pb-6 text-center">
                  <div className="text-3xl md:text-4xl font-bold text-primary mb-2">100K+</div>
                  <div className="text-sm text-muted-foreground">Sessions Completed</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6 pb-6 text-center">
                  <div className="text-3xl md:text-4xl font-bold text-primary mb-2">4.8
                    <Star className="w-5 h-5 inline-block ml-1 text-yellow-500" />
                  </div>
                  <div className="text-sm text-muted-foreground">Average Rating</div>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Quality Assurance */}
          {showQualityAssurance && (
            <Card className="bg-muted/50">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <Users className="w-10 h-10 text-primary flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">Quality Assurance & Professional Standards</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Every therapist on our platform undergoes rigorous verification and must maintain 
                      the highest professional standards. We ensure ongoing quality through continuous 
                      education, peer review, and patient feedback.
                    </p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-sm">Licensed and accredited professionals only</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-sm">Regular continuing education requirements</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-sm">Patient safety and privacy protocols</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-sm">Professional liability insurance coverage</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Medical Disclaimer for YMYL */}
          <div className="mt-8 p-6 bg-muted/30 rounded-lg border border-border">
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Professional Medical Disclaimer
            </h4>
            <p className="text-xs text-muted-foreground leading-relaxed">
              The information provided on this platform is for informational purposes only and does not 
              constitute medical advice. Always seek the advice of qualified health providers with any 
              questions regarding a medical condition. Never disregard professional medical advice or 
              delay in seeking it because of something you have read on this website. If you are 
              experiencing a mental health emergency, please call your local emergency services or crisis 
              hotline immediately.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpertiseAuthority;
